'use client';

import Image from 'next/image';
import { FC, useEffect, useRef } from 'react';

import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import cn from '@/helpers/cn';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { TransformationDto } from './types';

interface Props {
	data: TransformationDto;
}

const Transformation: FC<Props> = ({ data }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const parallaxRefs = useRef<HTMLDivElement[]>([]);

	const attachChubRef = (el: HTMLDivElement | null, index: number) => {
		if (el) parallaxRefs.current[index] = el;
	};

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const cards = wrapper?.querySelectorAll(`.${css.card}`);
		const titleLines = wrapper?.querySelectorAll(`.${css.title} p`);

		if (!wrapper || !titleLines) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: `(min-width: 992px)`,
					isMobile: `(max-width: 991px)`
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (cards) {
						gsap.from(cards, {
							y: 100,
							opacity: 0,
							stagger: 0.25,
							duration: 2,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top 50%'
							}
						});

						cards.forEach(card => {
							const numberEl = card.querySelector(`.${css.cardNumber} span:first-of-type`);
							if (!numberEl) return;

							const finalValue = parseInt(numberEl.textContent || '0', 10);

							gsap.fromTo(
								numberEl,
								{ innerText: 0 },
								{
									innerText: finalValue,
									duration: 2,
									ease: 'power1.out',
									snap: { innerText: 1 },
									scrollTrigger: {
										trigger: card,
										start: 'top 80%',
										toggleActions: 'play none none none'
									}
								}
							);
						});
					}

					parallaxRefs.current.forEach((el, i) => {
						gsap.to(el, {
							yPercent: i % 2 === 0 ? 30 : -30,
							rotation: i % 2 === 0 ? 12 : -10,
							scale: 1.15,
							ease: 'none',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top bottom',
								end: 'bottom top',
								scrub: true
							}
						});
					});

					if (isMobile) return;

					gsap.set(titleLines, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleLines, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: 0.15,
						scrollTrigger: {
							trigger: titleLines,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});
				}
			);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<div className={css.content}>
				<Typography uppercase variant="h2" tag="h2" className={css.title}>
					<StrapiRichContent content={data.title} />
				</Typography>

				<div className={css.cards}>
					{data.cards.map(card => (
						<div key={card.title} className={css.card}>
							<Typography variant="h6" tag="p" className={css.cardTitle}>
								{card.title}
							</Typography>

							<h3 className={css.cardNumber}>
								<span>{card.quantity}</span>

								<span>{card.quantitySign}</span>
							</h3>

							<Typography variant="h6-regular" tag="p" className={css.cardText}>
								{card.description}
							</Typography>
						</div>
					))}
				</div>

				<div ref={el => attachChubRef(el, 0)} className={cn(css.chub, css.chub1)}>
					<Image
						fill
						sizes="350px"
						alt=""
						src="/assets/images/about-us-page/transformation/chub-1.webp"
					/>
				</div>

				<div ref={el => attachChubRef(el, 1)} className={cn(css.chub, css.chub2)}>
					<Image
						fill
						sizes="640px"
						alt=""
						src="/assets/images/about-us-page/transformation/chub-2.webp"
					/>
				</div>

				<div ref={el => attachChubRef(el, 2)} className={cn(css.chub, css.chub3)}>
					<Image
						fill
						sizes="240px"
						alt=""
						src="/assets/images/about-us-page/transformation/chub-3.webp"
					/>
				</div>
			</div>
		</section>
	);
};

export default Transformation;
